import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faXmark, 
  faMagnifyingGlassPlus, 
  faMagnifyingGlassMinus, 
  faDownload, 
  faExpand, 
  faCompress 
} from '@fortawesome/free-solid-svg-icons';
import { getFile, getFileBlob } from './actions';
import { toast } from 'react-toastify';
import './documents.css';

const FileViewer = ({ isOpen, onClose, file, documentId }) => {
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fileType, setFileType] = useState(null);
  const modalRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen && file) {
      loadFileForViewing();
      document.addEventListener('keydown', handleKeyDown);
    } else {
      cleanup();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      cleanup();
    };
  }, [isOpen, file]);

  const cleanup = () => {
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
      setFileUrl(null);
    }
    setScale(1);
    setIsFullscreen(false);
    setFileType(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (isFullscreen) {
        exitFullscreen();
      } else {
        handleClose();
      }
    }
  };

  const loadFileForViewing = async () => {
    if (!file || !documentId) {
      console.error('File or documentId is missing');
      return;
    }

    try {
      setLoading(true);
      
      const blob = await getFileBlob(documentId, file.id);
      
      // ВИПРАВЛЕННЯ: Визначаємо тип файлу явно
      let actualFileType = blob.type;
      
      // Якщо тип application/octet-stream, визначаємо за розширенням або mtype
      if (actualFileType === 'application/octet-stream') {
        if (file.name.toLowerCase().endsWith('.pdf') || file.mtype === 'application/pdf') {
          actualFileType = 'application/pdf';
        } else if (file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) || 
                   file.mtype?.includes('image')) {
          actualFileType = file.mtype || 'image/jpeg';
        }
      }
      
      setFileType(actualFileType);
      
      // Створюємо URL з правильним типом
      const url = URL.createObjectURL(new Blob([blob], { type: actualFileType }));
      setFileUrl(url);
      
    } catch (error) {
      console.error('Error loading file for viewing:', error);
      //toast.error('Помилка завантаження файлу для перегляду');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!file || !documentId) return;
    
    try {
      await getFile(documentId, file.id, file.name);
    } catch (error) {
      console.error('Error downloading file:', error);
      //toast.error('Помилка завантаження файлу');
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.1));
  };

  const handleResetZoom = () => {
    setScale(1);
  };

  const enterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  const handleFullscreenToggle = () => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || 
                               document.webkitFullscreenElement ||
                               document.msFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleClose = () => {
    cleanup();
    onClose();
  };

  const isImageFile = () => {
    return fileType?.includes('image') || 
           file?.type === 'image' || 
           file?.mtype?.includes('image') ||
           file?.name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/);
  };

  const isPdfFile = () => {
    return fileType === 'application/pdf' || 
           file?.type === 'pdf' || 
           file?.mtype === 'application/pdf' ||
           file?.name?.toLowerCase().endsWith('.pdf');
  };

  const renderFileContent = () => {
    if (loading) {
      return (
        <div className="file-viewer-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Завантаження...</span>
          </div>
        </div>
      );
    }

    if (!file) {
      return (
        <div className="file-viewer-error">
          <p className="text-muted">Файл не знайдено</p>
        </div>
      );
    }

    if (!fileUrl) {
      return (
        <div className="file-viewer-error">
          <p className="text-muted">Не вдалося завантажити файл</p>
        </div>
      );
    }

    // Для зображень
    if (isImageFile()) {
      return (
        <div className="file-viewer-image-container">
          <img
            src={fileUrl}
            alt={file.name}
            style={{
              transform: `scale(${scale})`,
              maxWidth: 'none',
              maxHeight: 'none'
            }}
            className="file-viewer-image"
          />
        </div>
      );
    }

    // Для PDF
    if (isPdfFile()) {
      return (
        <div className="file-viewer-pdf-container">
          <iframe
            src={fileUrl}
            title={file.name}
            className="file-viewer-pdf"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: '0 0'
            }}
          />
        </div>
      );
    }

    // Для інших типів файлів
    return (
      <div className="file-viewer-unsupported">
        <div className="text-center">
          <p className="text-muted mb-3">Попередній перегляд недоступний для цього типу файлу</p>
          <button className="btn btn-primary" onClick={handleDownload}>
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Завантажити файл
          </button>
        </div>
      </div>
    );
  };

  const canZoom = file && (isImageFile() || isPdfFile());

  if (!isOpen) return null;

  return (
    <div 
      className={`file-viewer-overlay ${isFullscreen ? 'file-viewer-fullscreen' : ''}`}
      ref={modalRef}
    >
      <div 
        className="file-viewer-backdrop"
        onClick={handleClose}
      />
      
      <div className="file-viewer-container">
        <div 
          className="file-viewer-controls"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="file-viewer-controls-group">
            {/* Кнопки масштабу */}
            {canZoom && (
              <>
                <button
                  className="file-viewer-control-btn"
                  onClick={handleZoomOut}
                  disabled={scale <= 0.1}
                  title="Зменшити"
                >
                  <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
                </button>
                <button
                  className="file-viewer-control-btn"
                  onClick={handleResetZoom}
                  disabled={scale === 1}
                  title="Скинути масштаб"
                >
                  {Math.round(scale * 100)}%
                </button>
                <button
                  className="file-viewer-control-btn"
                  onClick={handleZoomIn}
                  disabled={scale >= 3}
                  title="Збільшити"
                >
                  <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                </button>
              </>
            )}
          </div>

          <div className="file-viewer-controls-group">
            {/* Кнопка повноекранного режиму */}
            <button
              className="file-viewer-control-btn"
              onClick={handleFullscreenToggle}
              title={isFullscreen ? 'Вийти з повноекранного режиму' : 'Повноекранний режим'}
            >
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
            </button>
            
            {/* Кнопка завантаження */}
            <button
              className="file-viewer-control-btn"
              onClick={handleDownload}
              title="Завантажити"
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
            
            {/* Кнопка закриття */}
            <button
              className="file-viewer-control-btn file-viewer-close-btn"
              onClick={handleClose}
              title="Закрити (ESC)"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>

        <div 
          className="file-viewer-content"
          ref={contentRef}
          onClick={(e) => e.stopPropagation()}
        >
          {renderFileContent()}
        </div>
      </div>
    </div>
  );
};

export default FileViewer;